import React, { useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { User, ShieldCheck, Sparkles, Lock, Upload } from "lucide-react";

const QUALIFICATIONS = ["BDS", "MDS", "Diploma", "DHA", "Other"];
const EXPERIENCE = ["0-1 Years", "1-3 Years", "3-5 Years", "5-10 Years", "10+ Years"];
const OPPORTUNITY_TYPES = ["Full-time", "Part-time", "Visiting Consultant", "Contract", "Internship", "Other"];

const initial = {
  full_name: "",
  mobile: "",
  email: "",
  city: "",
  qualification: "",
  experience: "",
  opportunity_types: ["Full-time"],
  locations: "",
  resume_name: "",
  agree: false,
};

async function postWithFallback(url, body, localKey) {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {}
  try {
    const list = JSON.parse(localStorage.getItem(localKey) || "[]");
    list.push({ ...body, submitted_at: new Date().toISOString() });
    localStorage.setItem(localKey, JSON.stringify(list));
  } catch {}
}

export default function HireMeDrawer({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => {
    setForm((f) => ({
      ...f,
      opportunity_types: f.opportunity_types.includes(t)
        ? f.opportunity_types.filter((x) => x !== t)
        : [...f.opportunity_types, t],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.mobile.trim() || !form.email.trim()) {
      toast.error("Please fill in name, mobile and email.");
      return;
    }
    if (!form.agree) {
      toast.error("Please accept the Terms of Service.");
      return;
    }
    setLoading(true);
    try {
      await postWithFallback("/api/staffing/job-seekers", form, "dyntra_hire_me_v1");
      toast.success("Profile submitted — we'll match you to open roles this week.");
      setForm(initial);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="hire-me-drawer"
      >
        <SheetHeader className="pb-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-[#F3EFFC] flex items-center justify-center shrink-0">
              <User className="h-5 w-5 text-[#4B2AAD]" />
            </div>
            <div>
              <SheetTitle className="font-display text-lg">
                Hire Me — Create Your Profile
              </SheetTitle>
              <SheetDescription className="text-xs">
                Create your professional profile and get discovered by verified clinics
                for the right opportunities.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={submit} className="pt-5 space-y-4">
          <Field label="Full Name" placeholder="Enter your full name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="hm-name" />
          <Field label="Mobile Number" placeholder="Enter your mobile number" value={form.mobile} onChange={(v) => set("mobile", v)} testid="hm-mobile" prefix="+91" />
          <Field label="Email Address" placeholder="Enter your email address" type="email" value={form.email} onChange={(v) => set("email", v)} testid="hm-email" />
          <Field label="Current City" placeholder="Enter your current city" value={form.city} onChange={(v) => set("city", v)} testid="hm-city" />

          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Qualification"
              placeholder="Select your qualification"
              value={form.qualification}
              onChange={(v) => set("qualification", v)}
              options={QUALIFICATIONS}
              testid="hm-qual"
            />
            <Select
              label="Years of Experience"
              placeholder="Select experience"
              value={form.experience}
              onChange={(v) => set("experience", v)}
              options={EXPERIENCE}
              testid="hm-exp"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Preferred Opportunity Type
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {OPPORTUNITY_TYPES.map((t) => {
                const active = form.opportunity_types.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className={`inline-flex items-center gap-1 rounded-full text-xs font-semibold border px-3 py-1.5 transition-colors ${
                      active
                        ? "bg-[#F3EFFC] text-[#4B2AAD] border-[#4B2AAD]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#4B2AAD]"
                    }`}
                  >
                    {t}
                    {active && <span className="ml-1 text-[10px]">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <Field
            label="Preferred Location(s)"
            placeholder="Search and select locations"
            value={form.locations}
            onChange={(v) => set("locations", v)}
            testid="hm-loc"
            hint="You can select multiple cities"
          />

          <div>
            <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Upload Resume
            </label>
            <label className="mt-2 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#4B2AAD]/25 bg-[#F5F3FB] p-4 text-center cursor-pointer">
              <Upload className="h-5 w-5 text-[#4B2AAD]" />
              <p className="text-xs text-gray-700 mt-2">
                <span className="font-bold">Drag &amp; drop</span> your resume here <br />
                or <span className="text-[#4B2AAD] font-bold underline">browse files</span>
              </p>
              <p className="text-[10px] text-gray-400 mt-1">
                {form.resume_name || "PDF, DOC, DOCX up to 5MB"}
              </p>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx"
                onChange={(e) =>
                  set("resume_name", e.target.files?.[0]?.name || "")
                }
              />
            </label>
          </div>

          <label className="flex items-start gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={(e) => set("agree", e.target.checked)}
              className="mt-0.5 accent-[#4B2AAD] h-4 w-4"
            />
            I agree to Dyntra&apos;s{" "}
            <a href="/policy/terms" className="text-[#4B2AAD] font-semibold">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/policy/privacy" className="text-[#4B2AAD] font-semibold">
              Privacy Policy
            </a>
            .
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="hm-submit"
            className="dyntra-btn-primary w-full py-3"
          >
            {loading ? "Submitting..." : "Continue →"}
          </button>

          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 text-center text-[10px] text-gray-500">
            <TrustCell icon={ShieldCheck} label="Verified profiles" sub="manually verified" />
            <TrustCell icon={Sparkles} label="Smart matching" sub="right opportunities" />
            <TrustCell icon={Lock} label="Private & secure" sub="your data is safe" />
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", prefix, hint, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <div className="mt-1 flex items-center rounded-xl border border-gray-200 focus-within:border-[#4B2AAD] focus-within:ring-2 focus-within:ring-[#4B2AAD]/15">
        {prefix && (
          <div className="pl-3 pr-2 py-2 text-sm text-gray-500 border-r border-gray-200">
            {prefix}
          </div>
        )}
        <input
          data-testid={testid}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 outline-none text-sm bg-transparent"
        />
      </div>
      {hint && <p className="text-[10px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Select({ label, value, onChange, placeholder, options, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm bg-white"
      >
        <option value="">{placeholder || "Select"}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function TrustCell({ icon: Icon, label, sub }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <Icon className="h-3.5 w-3.5 text-[#4B2AAD]" />
      <p className="text-[10px] font-bold text-gray-900 leading-none">{label}</p>
      <p className="text-[9px] text-gray-500 leading-none">{sub}</p>
    </div>
  );
}
